<!--
 * @Description: 访客计划
 * @Author: mdb
 * @Date: 2022-07-18 19:37:48
 * @LastEditTime: 2023-03-14 09:16:25
 * @LastEditors: cxh
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        ref="tableRef"
        tableId="DcCosLbuSalesClue"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item label="计划状态">
              <a-radio-group
                v-model:value="queryForm.status"
                optionType="button"
                :options="planStatusEnumAll"
                @change="handleQueryChange('search')"
              />
            </dc-form-item>
            <dc-form-item label="主客户代码">
              <a-input v-model:value.trim="queryForm.customerNumber" :maxlength="100" placeholder="请输入" allowClear />
            </dc-form-item>
            <dc-form-item label="客户名称">
              <a-input v-model:value.trim="queryForm.customerName" :maxlength="100" placeholder="请输入" allowClear />
            </dc-form-item>
            <dc-form-item label="目的">
              <ParamCodesSelect
                v-model:value="queryForm.purpose"
                :options="paramCodeData.fbu_follow_purpose"
                placeholder="请选择"
                allowClear
              />
            </dc-form-item>
            <dc-form-item label="计划标题">
              <a-input v-model:value.trim="queryForm.title" :maxlength="100" allowClear placeholder="请输入" />
            </dc-form-item>
            <dc-form-item label="组织部门">
              <CustomerAreaCascader type="search" appId="cos_fbu" v-model:ids="queryForm.deptIdList" />
            </dc-form-item>
            <dc-form-item label="客户负责人">
              <UserSelect v-model:value="queryForm.dutyBy" placeholder="请选择" allowClear />
            </dc-form-item>
            <dc-form-item label="时间筛选">
              <a-input-group compact>
                <a-select v-model:value="queryForm.timeType" style="width: 33%">
                  <a-select-option :value="0">创建时间</a-select-option>
                  <a-select-option :value="1">拜访时间</a-select-option>
                </a-select>
                <CosRangePicker
                  v-model:startTime="queryForm.startTime"
                  v-model:endTime="queryForm.endTime"
                  style="width: 67%"
                />
              </a-input-group>
            </dc-form-item>
          </a-form>
        </template>

        <template #button>
          <a-button v-permission="'fbu_visitor_add_btn'" type="primary" @click="triggerAddOrEdit()">新增</a-button>
          <ExportButton v-permission="'fbu_visitor_export_btn'" appId="cos_fbu" taskType="208" :search="queryForm"
            >导出</ExportButton
          >
        </template>

        <template #bodyCell="{ column, record, text }">
          <template v-if="column.dataIndex === 'status'">
            <dc-status :enums="statusEnum" :value="text" />
          </template>
          <!-- 当状态为《已完成》（status=3）时不可进行其它操作 -->
          <template v-if="column.dataIndex === 'action'">
            <SellerButton
              v-permission="'fbu_visitor_complete_btn'"
              type="link"
              :workCode="record.dutyBy"
              :disabled="record.status === 3"
              @click="handleVisitComplete(record)"
              >完成拜访</SellerButton
            >
            <a-button
              v-permission="'fbu_visitor_edit_btn'"
              type="link"
              :disabled="record.status === 3"
              @click="triggerAddOrEdit(record.id)"
              >编辑</a-button
            >
            <a-button
              v-permission="'fbu_visitor_del_btn'"
              type="link"
              :disabled="record.status === 3"
              @click="handleDelete(record)"
              >删除</a-button
            >
          </template>
        </template>
      </cos-table>
    </dc-layout-content>
  </dc-layout-main>
</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Modal from '@/hooks/modalType'
import { message } from '@dc-platform/dc-components'
import { planStatusEnum, remindTypeEnum } from '../dataConfig'
import { listByParamCodes } from '@/api/common'
import CustomerAreaCascader from '@/components/CustomerAreaCascader/index.vue'
import { visitorProgramList, visitorProgramDelete } from '@/api/gucangSales/followUpManage'
import type { ParamCodeItem } from '@/typings/common'
import type { FbuVisitorProgramParams, FbuVisitorProgramListItem } from '@/typings/api/followUpManage'
const router = useRouter()

// 计划状态枚举
const planStatusEnumAll = [{ label: '全部', value: undefined }, ...planStatusEnum]
const statusEnum = planStatusEnum.map((item) => ({
  name: item.label,
  value: item.value,
  color: item.color
}))
const paramCodeData: {
  [key: string]: ParamCodeItem[]
} = reactive({
  fbu_follow_purpose: [], // 拜访目的
  fbu_follow_mode: [] // 拜访方式（跟进方式）
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_fbu',
    paramCodes: ['fbu_follow_purpose', 'fbu_follow_mode']
  }).then((res) => {
    Object.assign(paramCodeData, res.data)
  })
}
onMounted(() => getByParamCodes())

const tableRef = ref()
const queryForm = reactive<FbuVisitorProgramParams>({
  timeType: 0
})

const columns = computed(() => [
  {
    title: '计划标题',
    dataIndex: 'title',
    cosType: 'link',
    path: '/fbu/visitorProgram/view',
    query: ['id'],
    fixed: 'left',
    width: 200
  },
  { title: '主客户代码', dataIndex: 'customerNumber' },
  { title: '客户名称', dataIndex: 'customerName', width: 200 },
  { title: '拜访方式', dataIndex: 'mode', dicData: paramCodeData.fbu_follow_mode },
  { title: '目的', dataIndex: 'purpose', dicData: paramCodeData.fbu_follow_purpose },
  { title: '计划状态', dataIndex: 'status' },
  { title: '访客提醒', dataIndex: 'remindType', dicData: remindTypeEnum },
  { title: '客户负责人', dataIndex: 'dutyByName' },
  { title: '拜访时间', dataIndex: ['startTime', 'endTime'], cosType: 'timeRange', sorter: true },
  { title: '创建人', dataIndex: 'createByName' },
  { title: '创建时间', dataIndex: 'createTime', sorter: true },
  { title: '备注', dataIndex: 'remark' },
  { title: '操作', dataIndex: 'action', width: 200, fixed: 'right' }
])
const getList = (queryForm: FbuVisitorProgramParams, callBack: (res: any) => void) => {
  visitorProgramList(queryForm).then((res) => {
    callBack(res)
  })
}
const handleQueryChange = (type: 'search' | 'fetch') => {
  if (type === 'search') {
    tableRef.value?.search()
  } else {
    tableRef.value?.fetch()
  }
}

// 新增编辑函数
const triggerAddOrEdit = (id?: string) => {
  if (id) {
    router.push({ path: '/fbu/visitorProgram/edit', query: { id } })
  } else {
    router.push('/fbu/visitorProgram/add')
  }
}
const handleVisitComplete = (row: FbuVisitorProgramListItem) => {
  router.push({ path: '/fbu/followUpRecord/visitingPlan', query: { planId: row.id } })
}
const handleDelete = (row: FbuVisitorProgramListItem) => {
  Modal.delete({
    name: row.title,
    onOk() {
      visitorProgramDelete(row.id).then(() => {
        message.success('删除计划成功')
        tableRef.value?.delFetch()
      })
    }
  })
}
</script>
<script lang="ts">
export default { name: 'DcCosFbuVisitorProgram' }
</script>

<style scoped></style>
